import React from "react";
import { Modal } from "../../components";
import { Panel as LegacyPanel, Loading } from "../../components/panels";
import { type IPagination, Pagination } from "../../pagination";
import NoteCreateModal, { type NoteState } from "../create/app";
import NoteRowCard from "./NoteRow";
import { useShortcutPanel } from "components/Shortcuts/hooks";
import { ButtonSecondary } from "components/Atoms/Button";
import SectionPanel from "components/panels/SectionPanel/SectionPanel";
import PanelSegment from "components/panels/PanelSegment/PanelSegment";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { useElementHeight } from "utilities/hookutils";
interface Props {
  mainArea: server.dto.MainAreas
  config: server.dto.LinkedNotePanelConfig
  useSectionPanel?: boolean
}

const NotesPanel = (props: Props) => {
  const getDefaultNoteState = (): NoteState => ({
    charactersRemaining: serverReference.constants.note.maxBodyLength,
    visibleToExternal: props.config.defaultVisibleToExternal,
    subject: "",
    body: "",
    subjects: [],
    noSubjectError: false,
    isSaving: false,
    noteError: null // We can use this in the future to show API errors
  });

  const [notes, setNotes] = React.useState<server.dto.Note[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [editingNote, setEditingNote] = React.useState<server.dto.Note>(undefined);
  const [pagination, setPagination] = React.useState<IPagination>({
    total: 0,
    page: 1,
    perPage: 10,
    maxPerPage: 10,
    pageCount: 0
  });

  const [noteState, setNoteState] = React.useState<NoteState>(getDefaultNoteState());

  const updateNoteState = (partial: Partial<NoteState>) => {
    setNoteState(prevNoteState => ({
      ...prevNoteState,
      ...partial
    }));
  };

  const reloadNotes = async (page = 1) => {
    setLoading(true);
    setPagination({ ...pagination, page });
    // const result = await noteApi.GetNotes(props.config.itemId, page, pagination.perPage);
    const result = await window.CoreApi.Note.List({
      mainArea: props.mainArea,
      page,
      perPage: pagination.perPage,
      parentId: props.config.itemId
    });
    setPagination(result.data.pagination);
    setNotes(result.data.data);
    setLoading(false);
  };

  window.refreshNotes = reloadNotes;

  const onFinishedSaving = async () => {
    setEditingNote(undefined);
    setNoteState(getDefaultNoteState());
    return reloadNotes();
  };

  const onNoteEdit = (note: server.dto.Note) => {
    let newNoteState = getDefaultNoteState();
    newNoteState = {
      ...newNoteState,
      visibleToExternal: note.visibleToExternal,
      subject: note.title || "",
      body: note.body || ""
    };
    setEditingNote(note);
    setNoteState(newNoteState);
  };

  const onClose = () => {
    if (editingNote) {
      setNoteState(getDefaultNoteState());
    }
    setEditingNote(undefined);
  };

  const { refToScrollTo } = useShortcutPanel({
    type: "note",
    label: props.config.headerLabel,
    totalItems: notes.length,
    onAddClicked: props.config.canAdd ? () => { setEditingNote(null); } : undefined
  });

  return <>
    <Panel
      useSectionPanel={props.useSectionPanel}
      title={props.config.headerLabel}
      isLoading={loading}
      hasData={notes.length > 0}
      addButton={props.config.canAdd && <ButtonSecondary onClick={() => { setEditingNote(null); }} label={RESX.Buttons.btnNew} />}
      panelRef={refToScrollTo}
      pagination={<Pagination
        paginationData={pagination}
        onPaginate={reloadNotes}
      />}
    >
      <div className="list-with-gaps line-layout">
        <div className="list-with-gaps-body">
          {notes.map(note => <NoteRowCard
            key={note.noteId}
            note={note}
            onEdit={onNoteEdit}
            onDeleted={reloadNotes}
            canDelete={note.permissions.canDelete}
            canEdit={note.permissions.canModify}
            config={props.config}
          />)}
        </div>
      </div>
    </Panel>
    <Modal
      title={editingNote === null ? RESX.Note.NewNote : RESX.Message.EditNote}
      handleClose={onClose}
      width="large"
      isOpen={editingNote !== undefined}
      hasAutoScrollBox
    >
      <NoteCreateModal
        mainArea={props.mainArea}
        config={props.config}
        existingNote={editingNote}
        onFinishedSaving={onFinishedSaving}
        noteState={noteState}
        onNoteStateChanged={updateNoteState}
      />
    </Modal>
  </>;
};

interface PanelProps {
  addButton: React.ReactNode;
  title: string;
  isLoading: boolean;
  panelRef: React.MutableRefObject<HTMLDivElement>;
  hasData: boolean;
  pagination: React.ReactNode;
  useSectionPanel: boolean;
}

const Panel = (props: React.PropsWithChildren<PanelProps>) => {
  const elHeight = useElementHeight();

  if (props.useSectionPanel) {
    let state: "loading" | "no-data" | "data";
    if (props.isLoading) state = "loading";
    else if (!props.hasData) state = "no-data";
    else state = "data";

    return <SectionPanel.Panel
      header={<>
        <PanelSegment.Title title={props.title} />
        {props.pagination}
        {props.addButton}
      </>}
      panelRef={props.panelRef}
    >
      {state === "loading" && <Loading height={elHeight.height} />}
      {state === "no-data" && <NoDataAvailableDiv />}
      {state === "data" && <div ref={elHeight.ref}>{props.children}</div>}

    </SectionPanel.Panel>;
  }

  return <LegacyPanel
    button={props.addButton}
    panelClassName="s1_related-feature-panel"
    panelRef={props.panelRef}
    title={props.title}
    isLoading={props.isLoading}
    hasData={props.hasData}
    pagination={props.pagination}
  >
    {props.children}
  </LegacyPanel>;
}

export default NotesPanel;